import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputLabel } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import BaseDrawer from "../../components/ui/BaseDrawer";
import BaseAutoSelect from "../../components/ui/BaseAutoSelect";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseAsyncCitySelect from "../../components/ui/BaseAsyncCitySelect";

import { useCountries } from "../../hooks/useCountries";
import {
  setWorkforceFilters,
  resetWorkforceFilters,
} from "../../store/filters/workforceFiltersSlice";
import {
  setFreelanceCompanyWorkforceFilters,
  resetFreelanceCompanyWorkforceFilters,
} from "../../store/filters/freelanceCompanyWorkforceFiltersSlice";
import {
  setTalentPoolFilters,
  resetTalentPoolFilters,
} from "../../store/filters/talentPoolFiltersSlice";

import {
  fetchAllSubQualificationTypes,
  fetchSubQualificationTypes,
} from "../../services/api/qualification";
import { fetchAllSubCategories } from "../../services/api/category";
import useAuth from "../../hooks/useAuth";
import { UserType } from "../../utils/enum";

const yesNoOptions = [
  { label: "Yes", value: "Yes" },
  { label: "No", value: "No" },
];

const WorkForceFilters = ({
  open,
  onClose,
  fetchWorkforce,
  isLoading,
  setFiltersChanged,
  talentPoolData = [],
  filterType = "",
}) => {
  const dispatch = useDispatch();
  const { authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const isWorkforceLike = filterType === "workforce" || filterType === "freelanceCompanyWorkforce";
  const filters = useSelector((state) =>
    filterType === "workforce"
      ? state.workforceFilters.value
      : filterType === "freelanceCompanyWorkforce"
        ? state.freelanceCompanyWorkforceFilters.value
        : state.talentPoolFilters.value,
  );

  const setFiltersAction =
    filterType === "workforce"
      ? setWorkforceFilters
      : filterType === "freelanceCompanyWorkforce"
        ? setFreelanceCompanyWorkforceFilters
        : setTalentPoolFilters;

  const resetFiltersAction =
    filterType === "workforce"
      ? resetWorkforceFilters
      : filterType === "freelanceCompanyWorkforce"
        ? resetFreelanceCompanyWorkforceFilters
        : resetTalentPoolFilters;
  const { countries } = useCountries();

  const [localFilters, setLocalFilters] = useState({
    fullName: "",
    country: { value: "", label: "" },
    city: { value: "", label: "" },
    talentPool: { value: "", label: "" },
    jobsCompleted: "",
    workedWith: "",
    backgroundCheck: "",
    certifications: { value: "", label: "" },
    equipment: { value: "", label: "" },
    skillSet: { value: "", label: "" },
  });

  const {
    isLoading: isUserAllQualificationsApiLoading,
    isError: isUserAllQualificationsApiError,
    refetch: refetchUserAllQualifications,
    data: equipmentData,
  } = useQuery({
    queryKey: ["fetchAllSubQualificationTypes"],
    queryFn: () => fetchAllSubQualificationTypes(),
  });

  const {
    isLoading: IssubQualificationsDataLoading,
    error: issubQualificationsDataError,
    data: certificationsData,
  } = useQuery({
    queryKey: ["subQualifications", 1],
    queryFn: () => fetchSubQualificationTypes(1),
  });

  const { data: skillsData } = useQuery({
    queryKey: ["subCategories"],
    queryFn: fetchAllSubCategories,
  });

  useEffect(() => {
    if (filters) {
      setLocalFilters((prev) => ({ ...prev, ...filters }));
    }
  }, [filters]);

  const handleCountryChange = (option) => {
    setLocalFilters((prev) => ({
      ...prev,
      country: { value: option.target?.value, label: option.target?.label },
      city: { value: "", label: "" },
    }));
  };

  const handleApplyFilters = () => {
    Object.entries(localFilters).forEach(([key, val]) => {
      const cleanValue = val?.target ? val.target : val;
      dispatch(setFiltersAction({ filterName: key, data: cleanValue }));
    });
    dispatch(setFiltersAction({ filterName: "pagination", data: { page: 0 } }));

    setTimeout(() => {
      fetchWorkforce?.();
      onClose();
    }, 0);
  };

  const handleResetFilters = () => {
    dispatch(resetFiltersAction());
    setLocalFilters({
      fullName: "",
      country: { value: "", label: "" },
      city: { value: "", label: "" },
      talentPool: { value: "", label: "" },
      jobsCompleted: "",
      workedWith: "",
      backgroundCheck: "",
      certifications: { value: "", label: "" },
      equipment: { value: "", label: "" },
      skillSet: { value: "", label: "" },
    });
    setFiltersChanged(true);
  };

  const hasFilters = Object.values(localFilters).some(
    (v) => v && (typeof v === "string" ? v.trim() !== "" : v.value || v.label),
  );

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      onApply={handleApplyFilters}
      title={
        <span
          style={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: "24px",
            color: "#686868",
            fontWeight: "400",
          }}
        >
          Search Criteria
        </span>
      }
      saveButtonText="Apply Filters"
      isLoading={isLoading}
      enabled={hasFilters}
      handleResetFilters={handleResetFilters}
    >
      <div>
        <div className="mb-5">
          <BaseTextField
            optional={false}
            required={true}
            labelShrink={false}
            label="Full Name"
            name="fullName"
            placeholder="Enter Full Name"
            value={localFilters.fullName}
            onChange={(e) => setLocalFilters({ ...localFilters, fullName: e.target.value })}
            isClearable
            className="!bg-white !rounded-md !border-none !shadow-[0_1px_3px_rgba(0,0,0,0.2)] focus:!shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-shadow duration-200"
            InputProps={{
              sx: {
                border: "none",
                boxShadow: "none",
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
              },
            }}
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            required={true}
            labelShrink={false}
            label="Country"
            name="country"
            placeholder="Select Country"
            options={countries || []}
            value={localFilters.country?.value || ""}
            onChange={handleCountryChange}
            isClearable
          />
        </div>

        <div className="mb-5">
          <BaseAsyncCitySelect
            required={true}
            labelShrink={false}
            label="City"
            placeholder="Select City"
            country={localFilters.country?.label}
            value={localFilters.city?.value || ""}
            onChange={(city) =>
              setLocalFilters((prev) => ({
                ...prev,
                city: city ? { value: city, label: city } : { value: "", label: "" },
              }))
            }
          />
        </div>

        {isWorkforceLike && !isCustomer && (
          <div className="mb-5">
            <BaseAutoSelect
              required={true}
              labelShrink={false}
              label="Talent Pool"
              name="talentPool"
              placeholder="Select Talent Pool"
              options={talentPoolData}
              value={localFilters.talentPool}
              onChange={(option) =>
                setLocalFilters({
                  ...localFilters,
                  talentPool: {
                    value: option.target?.value,
                    label: option.target?.label,
                  },
                })
              }
              isClearable
            />
          </div>
        )}

        {!isCustomer && (
          <>
            <div className="mb-5">
              <BaseTextField
                label="Jobs Completed"
                name="jobsCompleted"
                optional={false}
                labelShrink={false}
                placeholder="Enter Number"
                type="number"
                value={localFilters.jobsCompleted}
                onChange={(e) =>
                  setLocalFilters({ ...localFilters, jobsCompleted: e.target.value })
                }
                isClearable
              />
            </div>

            <div className="mb-5">
              <BaseAutoSelect
                label="Worked With"
                name="workedWith"
                required={true}
                labelShrink={false}
                options={yesNoOptions}
                value={localFilters.workedWith}
                onChange={(e) => setLocalFilters({ ...localFilters, workedWith: e.target.value })}
                isClearable
              />
            </div>

            <div className="mb-5">
              <BaseAutoSelect
                label="Background Check"
                name="backgroundCheck"
                required={true}
                labelShrink={false}
                options={yesNoOptions}
                value={localFilters.backgroundCheck}
                onChange={(option) =>
                  setLocalFilters({
                    ...localFilters,
                    backgroundCheck: option.target.value,
                  })
                }
                isClearable
              />
            </div>
          </>
        )}

        <div className="mb-5">
          <BaseAutoSelect
            label="Certifications"
            name="certifications"
            required={true}
            labelShrink={false}
            placeholder="Select Certification"
            options={certificationsData?.data?.map((certification) => ({
              value: certification.id,
              label: certification.name,
            }))}
            value={localFilters.certifications}
            onChange={(option) =>
              setLocalFilters({
                ...localFilters,
                certifications: {
                  value: option.target?.value,
                  label: option.target?.label,
                },
              })
            }
            isClearable
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            label="Equipment"
            name="equipment"
            required={true}
            labelShrink={false}
            placeholder="Select Equipment"
            options={equipmentData?.data?.data?.map((equipment) => ({
              value: equipment.id,
              label: equipment.name,
            }))}
            value={localFilters.equipment}
            onChange={(option) =>
              setLocalFilters({
                ...localFilters,
                equipment: {
                  value: option.target?.value,
                  label: option.target?.label,
                },
              })
            }
            isClearable
          />
        </div>

        <div className="mb-5">
          <BaseAutoSelect
            label="Skill Set"
            name="skillSet"
            required={true}
            labelShrink={false}
            placeholder="Select Skill"
            options={skillsData?.data?.map((skills) => ({
              value: skills.id,
              label: skills.subCategory,
            }))}
            value={localFilters.skillSet}
            onChange={(option) =>
              setLocalFilters({
                ...localFilters,
                skillSet: {
                  value: option.target?.value,
                  label: option.target?.label,
                },
              })
            }
            isClearable
          />
        </div>
      </div>
    </BaseDrawer>
  );
};

export default WorkForceFilters;
